/**
 * EVERYTHING THIS ROOM HAS SHARED, in one place: the room's report and each
 * view the chat built, every link with who it is for and whether it was read.
 *
 * Links used to be found only where they were made - the room's from the top
 * bar, a view's from its own header - so "what have I sent out of this room,
 * and did anyone open it" meant visiting each one. They are still MADE there:
 * this panel only lists them, so there is one place to share from.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { copyText } from '../../utils/clipboard';
import type { ShareLink } from '../../types';
import { shortDate } from './deliverables/provenance';

/** "Opened 3 times · last Oct 14", or "Not opened yet". */
export function readLine(link: Pick<ShareLink, 'view_count' | 'last_viewed_at'>, now: Date = new Date()): string {
  if (!link.view_count) return 'Not opened yet';
  const times = link.view_count === 1 ? 'once' : `${link.view_count} times`;
  const last = shortDate(link.last_viewed_at, now);
  return `Opened ${times}${last ? ` · last ${last}` : ''}`;
}

/** WHO A LINK IS FOR, edited in place: click the name, a box opens with Save
 *  and Cancel. Enter saves; Escape or a click anywhere else cancels. Emptied,
 *  the link is for anyone. */
function Recipient({ link, onSave }: { link: ShareLink; onSave: (recipient: string | null) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const box = useRef<HTMLSpanElement | null>(null);
  // A CLICK OUTSIDE PUTS IT BACK, unsaved, the same as Cancel.
  useEffect(() => {
    if (!editing) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setEditing(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [editing]);
  const shown = link.recipient ? `For ${link.recipient}` : 'Anyone with the link';
  const save = async () => {
    setSaving(true);
    try {
      await onSave(text.trim() || null);
      setEditing(false);
    } catch {
      /* the panel shows the error; the box stays open to try again */
    } finally {
      setSaving(false);
    }
  };
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setText(link.recipient ?? '');
          setEditing(true);
        }}
        title="Click to change who this link is for"
        className="min-w-0 truncate rounded text-left hover:bg-gray-100 dark:hover:bg-gray-700"
        data-testid={`shared-who-${link.id}`}
      >
        {shown}
      </button>
    );
  }
  return (
    <span ref={box} className="flex min-w-0 flex-1 items-center gap-1.5">
      <input
        autoFocus
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') void save();
          if (e.key === 'Escape') {
            // Only this box: the panel itself stays open.
            e.stopPropagation();
            setEditing(false);
          }
        }}
        placeholder="Who it is for (empty: anyone with the link)"
        aria-label="Who this link is for"
        className="min-w-0 flex-1 rounded border border-gray-300 px-1.5 py-0.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
        data-testid={`shared-who-input-${link.id}`}
      />
      <button
        type="button"
        onClick={() => void save()}
        disabled={saving}
        className="rounded bg-indigo-600 px-1.5 py-0.5 text-xs text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        Save
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        className="rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
      >
        Cancel
      </button>
    </span>
  );
}

function LinkRow({
  link,
  onStop,
  onRecipient,
}: {
  link: ShareLink;
  onStop: (link: ShareLink) => void;
  onRecipient: (link: ShareLink, recipient: string | null) => Promise<void>;
}) {
  const [copied, setCopied] = useState(false);
  const btn =
    'rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700';
  return (
    <li className="rounded-md border border-gray-200 px-2.5 py-2 dark:border-gray-700" data-testid={`shared-link-${link.id}`}>
      <div className="flex flex-wrap items-center gap-x-2 text-sm text-gray-900 dark:text-gray-100">
        <Recipient link={link} onSave={r => onRecipient(link, r)} />
        {!link.is_live && <span className="text-[11px] text-amber-700 dark:text-amber-400">expired</span>}
      </div>
      {link.boardroom_lead_title && (
        <div className="mt-0.5 text-xs text-indigo-700 dark:text-indigo-300" data-testid={`shared-lead-${link.id}`}>
          About the lead: {link.boardroom_lead_title}
        </div>
      )}
      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        {readLine(link)} · made {shortDate(link.created_at)}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          className={btn}
          onClick={async () => {
            if (await copyText(link.url)) {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            }
          }}
        >
          {copied ? 'Copied' : 'Copy link'}
        </button>
        {/* Marked so your own look is not counted as the recipient's. */}
        <a
          href={`${link.url}${link.url.includes('?') ? '&' : '?'}preview=1`}
          target="_blank"
          rel="noreferrer"
          className={btn}
        >
          Look at it
        </a>
        <button
          type="button"
          onClick={() => onStop(link)}
          className="rounded px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
          data-testid={`shared-stop-${link.id}`}
        >
          Stop sharing
        </button>
      </div>
    </li>
  );
}

export default function SharedPanel({
  boardroomId,
  titleOf,
}: {
  boardroomId: number;
  /** A view's name on this board, by its id. */
  titleOf: (deliverableId: number) => string | null;
}) {
  const [links, setLinks] = useState<ShareLink[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setLinks(await api.listShareLinks({ inBoardroomId: boardroomId }));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read what is shared');
    }
  }, [boardroomId]);
  useEffect(() => {
    void load();
  }, [load]);

  const rename = async (link: ShareLink, recipient: string | null) => {
    try {
      const updated = await api.updateShareLink(link.id, { recipient: recipient ?? '' });
      setLinks(ls => (ls ?? []).map(l => (l.id === link.id ? updated : l)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change who it is for');
      throw e;
    }
  };
  const stop = async (link: ShareLink) => {
    const who = link.recipient ? ` for ${link.recipient}` : '';
    if (!window.confirm(`Stop sharing this link${who}? Anyone who has it will no longer be able to open it.`)) return;
    try {
      await api.revokeShareLink(link.id);
      setLinks(ls => (ls ?? []).filter(l => l.id !== link.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not stop sharing that');
    }
  };

  const room = (links ?? []).filter(l => l.kind === 'BOARDROOM_REPORT');
  // ONE GROUP PER VIEW, named the way the board names it.
  const views = new Map<number, ShareLink[]>();
  for (const l of links ?? []) {
    if (l.kind !== 'BOARDROOM_CARD' || l.boardroom_deliverable_id == null) continue;
    views.set(l.boardroom_deliverable_id, [...(views.get(l.boardroom_deliverable_id) ?? []), l]);
  }

  return (
    <div className="space-y-4 p-3 text-sm" data-testid="shared-panel">
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      <section>
        <h3 className="mb-1.5 text-[11px] uppercase tracking-wide text-gray-400">The room's report</h3>
        {links === null ? (
          <p className="text-xs text-gray-400">Loading…</p>
        ) : room.length ? (
          <ul className="space-y-1.5">
            {room.map(l => (
              <LinkRow key={l.id} link={l} onStop={stop} onRecipient={rename} />
            ))}
          </ul>
        ) : (
          <p className="text-xs text-gray-500 dark:text-gray-400">Not shared yet. Share it from the top bar.</p>
        )}
      </section>
      <section>
        <h3 className="mb-1.5 text-[11px] uppercase tracking-wide text-gray-400">Views</h3>
        {links !== null && views.size === 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No views shared yet. A list or report the chat built can be shared from its header.
          </p>
        )}
        <div className="space-y-3">
          {Array.from(views.entries()).map(([id, ls]) => (
            <div key={id} data-testid={`shared-view-${id}`}>
              <div className="mb-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {titleOf(id) ?? 'A view'}
              </div>
              <ul className="space-y-1.5">
                {ls.map(l => (
                  <LinkRow key={l.id} link={l} onStop={stop} onRecipient={rename} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
